import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { IOption } from '@vtbhome/juicyfront/types';
import { useMemo } from 'react';
import { debounce } from '@vtbhome/juicyfront/utils/helpers';
import { BasicListParams, PaginationParametersRequest } from '@shared/model/common';

export interface useInfinitySelectParams<
    T extends {
        id: number;
    },
    R extends BasicListParams<T>,
    K extends PaginationParametersRequest & { search?: string | null },
> {
    listData?: R | null;
    labelProperty: keyof R['items'][number] | ((item: R['items'][number]) => string);
    valueProperty?: keyof R['items'][number];
    /* Обновление данных */
    isFetching?: boolean;
    setListParams: Dispatch<SetStateAction<K>>;
}

export const useInfinitySelect = <
    T extends {
        id: number;
    },
    R extends BasicListParams<T>,
    K extends PaginationParametersRequest & { search?: string | null },
>({
    listData,
    labelProperty,
    valueProperty,
    isFetching,
    setListParams,
}: useInfinitySelectParams<T, R, K>) => {
    const [isShowEmptyLoader, setIsShowEmptyLoader] = useState(true);

    const emptyOptions = useMemo(() => {
        /* Нужен пустой массив без потери ссылки после рендера */
        return [] as IOption[];
    }, []);

    const options: IOption[] = useMemo(() => {
        return (
            listData?.items?.map((schema) => {
                return {
                    label: String(typeof labelProperty === 'function' ? labelProperty(schema) : schema[labelProperty]),
                    value: String(schema[valueProperty || 'id']),
                };
            }) || emptyOptions
        );
    }, [emptyOptions, listData?.items]);

    const infinityScrollProps = useMemo(() => {
        return {
            dataLength: listData?.items.length || 0,
            hasMore: (listData?.items.length || 0) < (listData?.total || 0),
        };
    }, [listData]);

    const handleSearch = useCallback(
        debounce(
            (query: string, isPagination?: boolean | undefined) =>
                setListParams((prev) => {
                    setIsShowEmptyLoader(true);
                    return {
                        ...prev,
                        skip: isPagination ? (prev?.skip || 0) + 10 : null,
                        search: query,
                    };
                }),
            300
        ),
        [setListParams]
    );

    useEffect(() => {
        if (!isFetching) {
            setIsShowEmptyLoader(false);
        }
    }, [isFetching, isShowEmptyLoader]);

    return {
        options: isShowEmptyLoader ? emptyOptions : options,
        emptyOption: emptyOptions,
        isShowEmptyLoader: isShowEmptyLoader,
        infinityScrollProps,
        onSearch: handleSearch,
    };
};
